import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { JwtAuthService } from 'app/shared/services/auth/jwt-auth.service';
import {
  CartItem,
  DataOrderService,
  UserSearchResult,
  PrecheckoutOrderPayload,
  DeliveryData,
  SearchUserPayload,
  OrderStatus,
} from 'app/shared/services/data-order.service';
import { EMPTY, Observable, Subscription, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
  tap,
} from 'rxjs/operators';
import { OPTIONS } from 'app/shared/helpers/gmaps.helper';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AlertSnackbarComponent } from 'app/shared/components/alert-snackbar/alert-snackbar.component';
import { ListPharmaciesDialog } from 'app/views/products/active/active.component';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { Router } from '@angular/router';
import { Address } from 'ngx-google-places-autocomplete/objects/address';
import { MatSelectChange } from '@angular/material/select';
import { AppLoaderService } from 'app/shared/services/app-loader/app-loader.service';
import { PortalSettingsService } from 'app/shared/services/portal-settings.service';
import { AlertService } from 'app/shared/services/alert/alert.service';
import moment from 'moment';
import { DeliveryType, OrderStatusEnum } from 'app/shared/helpers/status-options.helper';

export interface Item {
  image: string;
  details: string;
  unitPrice: number;
  markUp: number;
  quantity: number;
  amount: number;
  itemID: string;
  freeText: boolean;
  isScript: boolean;
  reference: string;
  itemSchedule: string;
}

type MessageType = 'error' | 'success' | 'info';

interface UserPreferredDateTime {
  date: string;
  time: string;
}

@Component({
  selector: 'app-create-order',
  templateUrl: './create-order.component.html',
  styleUrls: ['./create-order.component.scss'],
  providers: [{ provide: MAT_DATE_LOCALE, useValue: 'en-AU' }],
})
export class CreateOrderComponent implements OnInit {
  @ViewChild('createOrderStepper', { static: false }) stepper: MatStepper;
  isAdmin: boolean;
  isBusiness: boolean;
  currentPharmacy;
  termsAccepted: boolean = false;
  loading: boolean = false;

  customerDetailsForm = new FormGroup({
    email: new FormControl('', Validators.required),
    firstName: new FormControl('', Validators.required),
    lastName: new FormControl('', Validators.required),
    mobile: new FormControl('', Validators.required),
  });

  addressDetailsForm = new FormGroup({
    address: new FormControl(''),
    streetName: new FormControl('', Validators.required),
    streetNumber: new FormControl('', Validators.required),
    unit: new FormControl('', []),
    city: new FormControl('', Validators.required),
    state: new FormControl('', Validators.required),
    postcode: new FormControl('', Validators.required),
    lat: new FormControl('', Validators.required),
    lng: new FormControl('', Validators.required),
  });

  deliveryDetailsForm = new FormGroup({
    deliveryAddress: new FormControl(''),
    deliveryStreetNumber: new FormControl(''),
    deliveryStreetName: new FormControl(''),
    deliveryUnit: new FormControl('', []),
    deliveryCity: new FormControl(''),
    deliveryState: new FormControl(''),
    deliveryPostcode: new FormControl(''),
    deliveryLat: new FormControl(''),
    deliveryLng: new FormControl(''),
    deliveryPartner: new FormControl('', { validators: [Validators.required] }),
    deliveryInstruction: new FormControl(''),
    additionalDeliveryInstructions: new FormControl(''),
    sameAsCustomerAddress: new FormControl(false),
  });

  pickUpDetailsForm = new FormGroup({
    pickUpNotes: new FormControl(''),
  });

  //Customer Details
  userSearchResults: UserSearchResult[] = [];
  selectedUser;
  healthProfile;
  currentAddress;
  searchingUser: boolean = false;
  obs: Subscription;
  options = OPTIONS;
  loggedIn: boolean = false;
  token: string;

  //Delivery Information
  deliveryType = new FormControl('delivery');
  addOTCItem = new FormControl({ value: '', disabled: true }, []);
  addScriptItem = new FormControl({ value: '', disabled: true }, []);
  addScriptToken = new FormControl({ value: '', disabled: true }, []);
  selectPharmacyStepControl = new FormControl('', [Validators.required]);
  addItemsStepControl = new FormControl('', [Validators.required]);

  deliveryPartners: any = [];
  deliveryInstructions = [
    'Leave on doorstep',
    'Leave at reception',
    'Ring door bell',
    'Knock on door',
    'Ring mobile',
    'Other',
  ];
  timezones = {
    SA: 'Australia/Adelaide',
    QLD: 'Australia/Brisbane',
    NT: 'Australia/Darwin',
    TAS: 'Australia/Hobart',
    VIC: 'Australia/Melbourne',
    WA: 'Australia/Perth',
    NSW: 'Australia/Sydney',
  };

  // deliveryAddressSearch = new FormControl('');
  deliveryaddressid;
  loadingDelivery: boolean = false;

  //PickUp Information
  startDate = new Date();

  //Items Table
  items: Item[] = [];
  displayedColumns: string[] = [
    'Image',
    'ItemDetails',
    'UnitPrice',
    'CustomerPrice',
    'QTY',
    'Amount',
  ];

  addItemType = 'otc';
  filteredOtcItemResults: Observable<any>;
  searchingItem: boolean = false;
  cartItems = [];
  scriptImgBase64 =
    'data:image/png;base64,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';

  pharmaciesList;

  //Prices
  deliveryFee = 0;
  serviceFee = 0;
  smallOrderFee: number = 0;
  promocode = '';
  isOrderCreated: boolean = false;
  orderId: number;
  orderSuccessMessage: string = 'Order Placed Successfully!';
  lastStepIndex: number = 3;
  groupedAllCustomerForms: FormGroup;
  groupedCustomerAddressDetailsForms: FormGroup;

  constructor(
    private jwtAuth: JwtAuthService,
    private dataOrder: DataOrderService,
    private _snackBar: MatSnackBar,
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<CreateOrderComponent>,
    private router: Router,
    private formBuilder: FormBuilder,
    private loader: AppLoaderService,
    private portalSettingsService: PortalSettingsService,
    private alertService: AlertService
  ) {
    this.isAdmin = this.jwtAuth.isAdmin() ? true : false;
    this.isBusiness = this.jwtAuth.isBusiness() ? true : false;

    this.filteredOtcItemResults = this.addOTCItem.valueChanges.pipe(
      tap((i: string) => (i.length > 0 ? (this.searchingItem = true) : EMPTY)),
      debounceTime(400),
      distinctUntilChanged(),
      switchMap((val: string) => {
        if (val.trim().length === 0) {
          this.searchingItem = false;
          // Return an empty observable to complete the stream.
          return EMPTY;
        }
        if (!this.isAdmin && !this.isBusiness) {
          return this.dataOrder
            .searchData(
              val.trim(),
              this.jwtAuth.getUser().location.locationid,
              'Medmate-Website'
            )
            .pipe(
              tap(() => (this.searchingItem = false)),
              map(response => response.drug_details),
              catchError(_ => {
                // console.error('API Error:', error);
                this.searchingItem = false;
                return of([]); // Return an empty array
              })
            );
        } else {
          return this.dataOrder
            .searchData(val.trim(), this.currentPharmacy.locationid, 'Medmate-Website')
            .pipe(
              tap(() => (this.searchingItem = false)),
              map(response => response.drug_details),
              catchError(_ => {
                // console.error('API Error:', error);
                this.searchingItem = false;
                return of([]); // Return an empty array
              })
            );
        }
      })
    );
  }

  ngOnInit(): void {
    if (this.jwtAuth.isPharmacyUser()) {
      this.lastStepIndex = 2;
    }

    this.groupedAllCustomerForms = this.formBuilder.group({
      customerDetails: this.customerDetailsForm,
      addressDetails: this.addressDetailsForm,
      deliveryDetails: this.deliveryDetailsForm,
      pickUpDetails: this.pickUpDetailsForm,
    });

    this.groupedCustomerAddressDetailsForms = this.formBuilder.group({
      customerDetails: this.customerDetailsForm,
      addressDetails: this.addressDetailsForm,
    });

    this.groupedCustomerAddressDetailsForms.statusChanges.subscribe(status => {
      if (status === 'VALID') {
        if (this.deliveryType.value === 'delivery') {
          this.deliveryDetailsForm.enable();
        } else {
          this.pickUpDetailsForm.enable();
        }
      } else {
        this.deliveryDetailsForm.reset();
        this.pickUpDetailsForm.reset();
        this.deliveryDetailsForm.disable();
        this.pickUpDetailsForm.disable();
      }
    });

    this.deliveryDetailsForm.get('deliveryPartner').valueChanges.subscribe(value => {
      this.setDeliveryFee(value);
    });

    this.deliveryType.valueChanges.subscribe(value => {
      if (this.token && this.groupedCustomerAddressDetailsForms.valid) {
        if (value === 'delivery') {
          this.groupedAllCustomerForms.removeControl('pickUpDetails');
          this.groupedAllCustomerForms.addControl('deliveryDetails', this.deliveryDetailsForm);
          this.deliveryDetailsForm.enable();
        } else {
          this.groupedAllCustomerForms.removeControl('deliveryDetails');
          this.groupedAllCustomerForms.addControl('pickUpDetails', this.pickUpDetailsForm);
          this.pickUpDetailsForm.enable();
        }
      }
    });
    this.addressDetailsForm.disable();
    this.customerDetailsForm.statusChanges.subscribe(status => {
      if (this.token) {
        if (status === 'VALID') {
          this.addressDetailsForm.enable();
        } else {
          this.addressDetailsForm.disable();
        }
      }
    });

    this.obs = this.customerDetailsForm.controls.email.valueChanges
      .pipe(debounceTime(500))
      .subscribe(() => {
        this.searchUser();
      });

    if (this.isAdmin || this.isBusiness) {
      this.loading = true;
      this.dataOrder.getPharmacyList({ statuses: '1,2,3,4,5,6,7' }).subscribe(res => {
        this.pharmaciesList = res;
        this.loading = false;
        if (this.currentPharmacy == null) {
          this.changePharmacy();
        }
      });
    } else {
      this.currentPharmacy = {
        locationname: this.jwtAuth.getUser().displayName,
        locationid: this.jwtAuth.getUser().locationid,
      };
    }

    this.deliveryType.valueChanges.subscribe(change => {
      if (change == 'pick up') {
        this.deliveryFee = 0;
      }
    });
  }

  get canEnableCreateOrderButton() {
    if (this.stepper?.selectedIndex !== this.lastStepIndex) {
      return { canEnable: true, reason: 'Please complete the steps and go to Review Order' };
    } else if (!this.currentPharmacy?.locationid) {
      return { canEnable: true, reason: 'Please select a pharmacy location' };
    } else if (this.addOTCItem.disabled) {
      return {
        canEnable: true,
        reason: 'Please ensure all required customer details are filled',
      };
    } else if (this.items.length === 0) {
      return { canEnable: true, reason: 'Please add items to the order' };
    } else {
      return { canEnable: false, reason: '' };
    }
  }

  onItemChangeQty(index, sign) {
    if (sign === '+') {
      this.items[index].quantity++;
    } else if (sign === '-') {
      this.items[index].quantity--;
    }
  }

  deleteItem(el) {
    this.items = this.items.filter(item => item.details != el.details);
    if (this.items.length === 0) {
      this.addItemsStepControl.reset();
    }
  }

  calculateCustomerPrice(item) {
    return (item.unitPrice + item.markUp).toFixed(2);
  }

  calculateTotalPrice(item) {
    return (this.calculateCustomerPrice(item) * item.quantity).toFixed(2);
  }

  calculateSubtotal() {
    let subtotal = 0;
    this.items.forEach(item => {
      subtotal += this.calculateCustomerPrice(item) * item.quantity;
    });
    return subtotal;
  }

  calculateTotal() {
    let total = this.calculateSubtotal() + this.deliveryFee + this.serviceFee;
    return total;
  }

  calculateDeliveryFee() {
    return this.deliveryFee.toFixed(2);
  }

  calculateServiceFee() {
    return (this.serviceFee + this.smallOrderFee).toFixed(2);
  }

  searchUser() {
    const email = this.getCustomerDetailsFormValue('email');
    if (typeof email == 'object' || email === '') return;
    this.searchingUser = true;
    const searchUserPayload: SearchUserPayload = {
      searchTerm: email,
      locationId: this.currentPharmacy.locationid,
    };
    this.dataOrder.searchUser(searchUserPayload).subscribe(
      res => {
        this.searchingUser = false;
        this.userSearchResults = res;
      },
      err => {
        this.searchingUser = false;
      }
    );
  }

  selectUser(event) {
    this.searchingUser = false;
    const selectedUser = event.option.value;
    if (selectedUser === '') return;
    this.addressDetailsForm.reset();
    this.deliveryDetailsForm.reset();
    this.pickUpDetailsForm.reset();
    const selectedUserEmail =
      typeof selectedUser === 'string' ? selectedUser : selectedUser?.username;
    const existingUserIndex = this.userSearchResults.findIndex(
      user => user.username === selectedUserEmail
    );
    if (existingUserIndex !== -1) {
      this.selectedUser = this.userSearchResults[existingUserIndex];
    } else {
      const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
      this.selectedUser = selectedUser;
      if (!emailRegex.test(this.selectedUser)) {
        this.showMessage('The username must be a valid email address.', 'error');
        return;
      }
    }
    if (existingUserIndex === -1) {
      if (this.getCustomerDetailsFormValue('firstName') === '') {
        this.showMessage('Please enter the first name before adding a new user.', 'error');
        return;
      }
      this.customerDetailsForm.controls.email.setValue(selectedUser);
    } else {
      this.deliveryDetailsForm.get('sameAsCustomerAddress').setValue(false);
      const userAddress = this.selectedUser.currentAddress;
      const customerFormcontrols = this.customerDetailsForm.controls;
      const addressFormcontrols = this.addressDetailsForm.controls;
      customerFormcontrols.email.setValue(this.selectedUser.username || '');
      customerFormcontrols.firstName.setValue(this.selectedUser.firstname || '');
      customerFormcontrols.lastName.setValue(this.selectedUser.lastname || '');
      customerFormcontrols.mobile.setValue(this.selectedUser.mobile || '');
      if (Object.keys(userAddress).length > 0) {
        this.currentAddress = userAddress;
        this.deliveryaddressid = this.currentAddress.deliveryaddressid;
        let splitAddress = [];
        if (userAddress.addressline1.includes(',')) {
          splitAddress = userAddress.addressline1.split(',');
        } else if (userAddress.addressline1.includes('/')) {
          splitAddress = userAddress.addressline1.split('/');
        } else {
          splitAddress = userAddress.addressline1.split(' ');
        }
        const addressLineSeparatorRegex = /[\/,]/; // This regex matches '/' or ','
        const includesAddressLineSeparator = addressLineSeparatorRegex.test(
          userAddress.addressline1
        );

        if (includesAddressLineSeparator) {
          addressFormcontrols.unit.setValue(splitAddress[0] || '');
          addressFormcontrols.streetNumber.setValue(splitAddress[1].split(' ')[0] || '');
          addressFormcontrols.streetName.setValue(
            splitAddress[1].split(' ').slice(1).join(' ') || ''
          );
        } else {
          addressFormcontrols.streetNumber.setValue(splitAddress[0]);
          addressFormcontrols.streetName.setValue(splitAddress.slice(1).join(' '));
        }
        addressFormcontrols.city.setValue(userAddress.suburb);
        addressFormcontrols.state.setValue(userAddress.state);
        addressFormcontrols.postcode.setValue(userAddress.postcode);
        addressFormcontrols.lat.setValue(userAddress.lattitude);
        addressFormcontrols.lng.setValue(userAddress.longitude);
      } else {
        this.addressDetailsForm.reset();
        this.deliveryDetailsForm.reset();
      }
    }
    this.login();
  }

  login() {
    this.showMessage(`Authenticating User`, 'info');
    this.jwtAuth.login(this.customerDetailsForm.controls.email.value, 2).subscribe(
      res => {
        this.token = res['success'].token;

        if (this.customerDetailsForm.valid && this.token) {
          this.addressDetailsForm.enable();
        }

        this.healthProfile = res['success'].healthprofile[0];
        this.loggedIn = true;
        this.addOTCItem.enable();
        this.addScriptItem.enable();
        this.addScriptToken.enable();
        this.showMessage(`User Authenticated`);
      },
      err => {
        if (typeof this.selectedUser === 'string') {
          this.showMessage('Creating new user...', 'info');
          this.register();
        } else {
          this.showMessage(
            err.error?.message || (err.error.error?.username && err.error.error.username[0]),
            'error'
          );
        }
      }
    );
  }

  showMessage(message: string, messageType: MessageType = 'success', duration: number = 3000) {
    const panelClassMap = {
      error: 'mm-bg-pink',
      success: 'mm-bg-green',
      info: 'mm-bg-warn',
    };

    const panelClass = panelClassMap[messageType] || '';

    if (duration === -1) {
      duration = undefined;
    }

    this._snackBar.openFromComponent(AlertSnackbarComponent, {
      panelClass: [panelClass],
      data: {
        msg: message,
        action: 'dismiss',
      },
      verticalPosition: 'top',
      horizontalPosition: 'center',
      duration: duration,
    });
  }

  // check if a form control is required and touched
  isRequiredAndTouched(control: FormControl): boolean {
    return control.hasError('required') && control.touched;
  }

  register(length = 16) {
    let generatedPassword = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

    for (var i = 0; i < length; i++) {
      generatedPassword += characters.charAt(Math.floor(Math.random() * characters.length));
    }

    const data = {
      firstname: this.customerDetailsForm.controls.firstName.value,
      username: this.customerDetailsForm.controls.email.value,
      password: generatedPassword,
      c_password: generatedPassword,
      accept_terms: 1,
      type: 2,
    };

    this.jwtAuth.register(data).subscribe(
      (res: any) => {
        this.selectedUser = res['success'].user_details;
        this.token = res['success'].token;
        this.healthProfile = res['success'].healthprofile;
        this.healthProfile.profileid = this.healthProfile.userid;
        this.addOTCItem.enable();
        this.addScriptItem.enable();
        this.addScriptToken.enable();
        this.showMessage(`User has been created.`);
      },
      (err: any) => {
        this.showMessage(err.error.error.username[0], 'error');
      }
    );
  }

  getCustomerDetailsFormValue(controlName: string) {
    return this.customerDetailsForm.controls[controlName].value;
  }

  getAddressDetailsFormValue(controlName: string) {
    return this.addressDetailsForm.controls[controlName].value;
  }

  getDeliveryDetailsFormValue(controlName: string) {
    return this.deliveryDetailsForm.controls[controlName].value;
  }

  public handleAddressChange(address: Address) {
    const filteredAddress = this.filterAddress(address, '#address-input');
    const controls = this.addressDetailsForm.controls;
    controls.unit.setValue(filteredAddress.subpremise);
    controls.streetNumber.setValue(filteredAddress.streetNumber);
    controls.streetName.setValue(filteredAddress.streetName);
    controls.city.setValue(filteredAddress.suburb);
    controls.state.setValue(filteredAddress.state);
    controls.postcode.setValue(filteredAddress.postcode);
    controls.lat.setValue(filteredAddress.lat);
    controls.lng.setValue(filteredAddress.lng);
  }

  setNewDeliveryAddress(address) {
    const filteredAddress = this.filterAddress(address, '#delivery_address');
    const controls = this.deliveryDetailsForm.controls;
    // controls.deliveryAddress.setValue(filteredAddress.streetName);
    controls.deliveryStreetNumber.setValue(filteredAddress.streetNumber);
    controls.deliveryStreetName.setValue(filteredAddress.streetName);
    controls.deliveryUnit.setValue(filteredAddress.subpremise);
    controls.deliveryCity.setValue(filteredAddress.suburb);
    controls.deliveryState.setValue(filteredAddress.state);
    controls.deliveryPostcode.setValue(filteredAddress.postcode);
    controls.deliveryLat.setValue(filteredAddress.lat);
    controls.deliveryLng.setValue(filteredAddress.lng);

    const validUnit = Boolean(controls.deliveryUnit.value)
      ? `${controls.deliveryUnit.value}/`
      : '';

    const addressData = {
      firstname: this.getCustomerDetailsFormValue('firstName'),
      addresstype: 1,
      addressline1: `${validUnit}${controls.deliveryStreetNumber.value} ${controls.deliveryStreetName.value}`,
      suburb: controls.deliveryCity.value,
      postcode: controls.deliveryPostcode.value,
      state: controls.deliveryState.value,
      latitude: controls.deliveryLat.value,
      longitude: controls.deliveryLng.value,
    };

    this.dataOrder.addDeliveryAddress(addressData, this.token).subscribe(
      res => {
        if (res.msg === 'Delivery Address Added') {
          this.deliveryaddressid = res.deliveryaddressid;
          this.getDeliveryPartners(
            this.currentPharmacy.locationid,
            res.deliveryaddressid,
            this.calculateSubtotal()
          );
          this.showMessage('Delivery Address Updated Successfully');
        } else {
          this.showMessage(res.msg, 'error');
        }
      },
      err => {
        this.showMessage(err.error.message, 'error');
      }
    );
  }

  filterAddress(gmapAddress: Address, inputId: string) {
    const formatted = {
      subpremise: '',
      streetNumber: '',
      streetName: '',
      suburb: '',
      state: '',
      postcode: '',
      lat: null,
      lng: null,
    };

    if (gmapAddress.types[0] !== 'street_address') {
      formatted.subpremise = gmapAddress.name;
    }

    gmapAddress.address_components.forEach(comp => {
      if (comp.types.includes('subpremise')) {
        formatted.subpremise = comp.long_name;
      }
      if (comp.types.includes('street_number')) {
        formatted.streetNumber = comp.long_name;
      }
      if (comp.types.includes('route')) {
        formatted.streetName = comp.long_name;
      }
      if (comp.types.includes('locality')) {
        formatted.suburb = comp.long_name;
      }
      if (comp.types.includes('administrative_area_level_1')) {
        formatted.state = comp.short_name;
      }
      if (comp.types.includes('postal_code')) {
        formatted.postcode = comp.long_name;
      }
    });
    formatted.lat = gmapAddress.geometry.location.lat();
    formatted.lng = gmapAddress.geometry.location.lng();
    // if (!gmapAddress.address_components[0].types.includes('subpremise')) {
    //   const addr = document.querySelector(inputId);
    //   const route = gmapAddress.address_components.filter(a => a.types.includes('route'))[0]
    //   if (addr['value'].indexOf(route['long_name']) != -1) {
    //     formatted.subpremise = addr['value'].substring(0, addr['value'].indexOf(route['long_name']) - 2)
    //   } else {
    //     formatted.subpremise = addr['value'].substring(0, addr['value'].indexOf(route['short_name']) - 2)
    //   }
    // }
    return formatted;
  }

  getAddressData() {
    const firstName = this.getCustomerDetailsFormValue('firstName');
    const lastName = this.getCustomerDetailsFormValue('lastName');
    const mobile = this.getCustomerDetailsFormValue('mobile');
    const unit = this.getAddressDetailsFormValue('unit');
    const streetNumber = this.getAddressDetailsFormValue('streetNumber');
    const streetName = this.getAddressDetailsFormValue('streetName');
    // const address = this.getAddressDetailsFormValue('address');
    const city = this.getAddressDetailsFormValue('city');
    const postcode = this.getAddressDetailsFormValue('postcode');
    const state = this.getAddressDetailsFormValue('state');
    const lat = this.getAddressDetailsFormValue('lat');
    const lng = this.getAddressDetailsFormValue('lng');

    const validUnit = Boolean(unit) ? `${unit}/` : '';

    const addressData = {
      firstname: firstName,
      lastname: lastName,
      mobile: mobile,
      addresstype: "1",
      addressline1: `${validUnit}${streetNumber} ${streetName}`,
      suburb: city,
      postcode: postcode,
      state: state,
      latitude: lat,
      longitude: lng,
    };

    return addressData;
  }

  addDeliveryAddress() {
    // const controls = this.deliveryDetailsForm.controls
    const addressData = this.getAddressData();
    if (this.deliveryDetailsForm.get('sameAsCustomerAddress').value === false) {
      return;
    } else if (
      this.currentAddress?.addressline1 === addressData.addressline1 &&
      this.currentAddress?.lattitude === addressData.latitude &&
      this.currentAddress?.longitude === addressData.longitude
    ) {
      // controls.deliveryAddress.setValue(addressData.addressline1)
      // controls.deliveryStreetNumber.setValue(controls.streetNumber.value)
      // controls.deliveryStreetName.setValue(controls.streetName.value)
      // controls.deliveryUnit.setValue(controls.unit.value)
      // controls.deliveryCity.setValue(controls.city.value)
      // controls.deliveryState.setValue(controls.state.value)
      // controls.deliveryLat.setValue(controls.lat.value)
      // controls.deliveryLng.setValue(controls.lng.value)
      this.getDeliveryPartners(
        this.currentPharmacy.locationid,
        this.currentAddress.deliveryaddressid,
        this.calculateSubtotal()
      );
    } else {
      this.dataOrder.addDeliveryAddress(addressData, this.token).subscribe(
        res => {
          if (res.msg === 'Delivery Address Added') {
            this.deliveryaddressid = res.deliveryaddressid;
            this.getDeliveryPartners(
              this.currentPharmacy.locationid,
              res.deliveryaddressid,
              this.calculateSubtotal()
            );
            this.showMessage('Delivery Address Updated Successfully');
          } else {
            this.showMessage(res.msg, 'error');
          }
        },
        err => {
          this.showMessage(err.error.message, 'error');
        }
      );
    }
  }

  getDeliveryPartners(locationid, deliveryaddressid, ordertotal) {
    this.loadingDelivery = true;
    this.showMessage('Loading Delivery Partners. Please wait...', 'info', -1);
    const data: DeliveryData = {
      locationid: locationid,
      deliveryaddressid: deliveryaddressid,
      ordertotal: ordertotal,
      check_soh: 0,
    };
    this.portalSettingsService.portalSettings.subscribe(settings => {
      data['Source'] = settings.source;
      if (this.portalSettingsService.getDomain().includes('medmate')) {
        const businessId = this.currentPharmacy.businessid;
        if (businessId === 2) {
          data['Source'] = settings.source + '-dds';
        } else if (businessId === 4) {
          data['Source'] = settings.source + '-amcal';
        }
      }
      if (ordertotal <= 0) data.ordertotal = 10;
      this.dataOrder.getDeliveryPartners(data, this.token).subscribe(
        res => {
          this.deliveryPartners = res['deliveryoptions'];
          this.deliveryDetailsForm.get('deliveryPartner').enable();
          this.loadingDelivery = false;
          this.showMessage(
            'Delivery Partners loaded successfully. Please now choose a delivery partner.'
          );
        },
        err => {
          this.deliveryDetailsForm.reset();
          this.loadingDelivery = false;
          this.showMessage(err.error.msg, 'error');
        }
      );
    });
  }

  setDeliveryFee(event: MatSelectChange) {
    const option = this.deliveryPartners.find(partner => partner.partnername == event.value);
    if (option) {
      this.deliveryFee = parseFloat(option.finaldeliveryfee);
    }
  }

  get isMedmatePortal(): boolean {
    return this.portalSettingsService.settings.source === 'Medmate-Portal';
  }

  addToCart(event) {
    const selected = event.option.value;
    this.searchingItem = false;
    if (selected === '') return;
    const item = {
      image: '',
      details: '',
      unitPrice: 0.0,
      markUp: this.isMedmatePortal ? 1.5 : 0,
      quantity: 1,
      amount: 0.0,
      itemID: 'MMB-O000000000',
      freeText: false,
      isScript: false,
      reference: '',
      itemSchedule: '',
    };
    if (this.addItemType == 'otc') {
      item.image = 'assets/images/svg-icons/medicine.svg';
      if (typeof selected === 'string') {
        item.details = selected;
        item.freeText = true;
      } else {
        item.image = selected.PhotoID_s3;
        item.details = selected.drug;
        item.unitPrice =
          parseFloat(selected.price) - parseFloat(selected.PMA) <= 0
            ? 0
            : parseFloat(selected.price) - parseFloat(selected.PMA);
        item.markUp = this.isMedmatePortal ? parseFloat(selected.PMA) : 0;
        item.amount = parseFloat(selected.price);
        item.itemID = selected.drugcode;
        item.reference = selected.reference;
        item.itemSchedule = selected.ItemSchedule;
      }
      //adding free text otc item
      if (item.freeText == true) {
        const dialogRef = this.dialog.open(SelectScheduleDialog, {
          width: '30%',
          data: selected,
          disableClose: true,
        });
        dialogRef.afterClosed().subscribe(res => {
          if (res == 'cancel') {
            return;
          } else {
            item.itemSchedule = res;
            this.addOTCItem.setValue('');
            this.items = [...this.items, item];
          }
        });
      } else {
        //adding otc item from catalogue
        this.addOTCItem.setValue('');
        this.items = [...this.items, item];
      }
    } else if ((this.addItemType = 'paper')) {
      item.image = 'assets/images/svg-icons/script.svg';
      if (this.termsAccepted == false) {
        const dialogRef = this.dialog.open(ConsentDialog, {
          width: '30%',
          data: '',
        });

        dialogRef.afterClosed().subscribe(code => {
          //did not consent to script terms
          if (code != 1) {
            return;
          } else {
            //agree to script terms
            this.termsAccepted = true;
            if (typeof selected === 'string') {
              item.details = selected;
              item.freeText = true;
              item.isScript = true;
              item.markUp = 0;
            } else {
              item.image = selected.PhotoID_s3;
              item.details = selected.drug;
              item.unitPrice =
                parseFloat(selected.price) - parseFloat(selected.PMA) <= 0
                  ? 0
                  : parseFloat(selected.price) - parseFloat(selected.PMA);
              item.amount = parseFloat(selected.price);
              item.itemID = selected.drugcode;
              item.itemSchedule = selected.ItemSchedule;
            }
            //adding free text script item on first time consenting
            if (item.freeText == true) {
              const dialogRef = this.dialog.open(SelectScheduleDialog, {
                width: '30%',
                data: selected,
                disableClose: true,
              });
              dialogRef.afterClosed().subscribe(res => {
                if (res == 'cancel') {
                  return;
                } else {
                  const dialogRef = this.dialog.open(UploadPaperScriptDialog, {
                    width: '30%',
                    data: res,
                    disableClose: true,
                  });
                  dialogRef.afterClosed().subscribe(res => {
                    const addPrescriptionData = {
                      prescriptiontype: '3',
                      prescriptiontitle: item.details,
                      prescriptionimage: '',
                      prescriptionbackimage: '',
                      barcode: '',
                      prescriptionuserid: this.healthProfile.profileid,
                      profileid: this.selectedUser.userid,
                      medilistid: '',
                      device_id: '',
                      type: 2,
                      application_id: 2,
                    };
                    if (res.prescriptionimage) {
                      addPrescriptionData.prescriptionimage = res['prescriptionimage'];
                    } else {
                      addPrescriptionData.prescriptionimage = this.scriptImgBase64;
                    }
                    item.itemSchedule = res.schedule;
                    this.showMessage('Adding Prescription, Please Wait...', 'info');
                    this.dataOrder
                      .addPrescriptionV2(addPrescriptionData, this.token)
                      .subscribe(
                        res => {
                          item.itemID = res.prescriptionItems.prescriptionid;
                          item.image = res.scriptsInfo.tokenimage_s3;
                          this.addScriptItem.setValue('');
                          this.items = [...this.items, item];
                          this.showMessage('Prescription added successfully.', 'success');
                        },
                        _ => {
                          this.showMessage(
                            'Failed to add prescription. Please try again.',
                            'error'
                          );
                        }
                      );
                  });
                }
              });
            } else {
              //adding script item from catalogue
              this.addScriptItem.setValue('');
              this.items = [...this.items, item];
            }
          }
        });
      } else {
        //already consented to terms
        if (typeof selected === 'string') {
          item.details = selected;
          item.freeText = true;
          item.isScript = true;
          item.markUp = 0;
        } else {
          item.image = selected.PhotoID_s3;
          item.details = selected.drug;
          item.unitPrice =
            parseFloat(selected.price) - parseFloat(selected.PMA) <= 0
              ? 0
              : parseFloat(selected.price) - parseFloat(selected.PMA);
          item.amount = parseFloat(selected.price);
          item.itemID = selected.drugcode;
        }
        //adding script free text item
        if (item.freeText == true) {
          const dialogRef = this.dialog.open(SelectScheduleDialog, {
            width: '30%',
            data: selected,
            disableClose: true,
          });
          dialogRef.afterClosed().subscribe(res => {
            if (res == 'cancel') {
              return;
            } else {
              const dialogRef = this.dialog.open(UploadPaperScriptDialog, {
                width: '30%',
                data: res,
                disableClose: true,
              });
              dialogRef.afterClosed().subscribe(res => {
                const addPrescriptionData = {
                  prescriptiontype: '3',
                  prescriptiontitle: item.details,
                  prescriptionimage: '',
                  prescriptionbackimage: '',
                  barcode: '',
                  prescriptionuserid: this.healthProfile.profileid,
                  profileid: this.selectedUser.userid,
                  medilistid: '',
                  device_id: '',
                  type: 2,
                  application_id: 2,
                };
                if (res.prescriptionimage) {
                  addPrescriptionData.prescriptionimage = res['prescriptionimage'];
                } else {
                  addPrescriptionData.prescriptionimage = this.scriptImgBase64;
                }
                item.itemSchedule = res.schedule;
                this.dataOrder
                  .addPrescriptionV2(addPrescriptionData, this.token)
                  .subscribe(res => {
                    item.itemID = res.prescriptionItems.prescriptionid;
                    item.image = res.scriptsInfo.tokenimage_s3;
                    this.addScriptItem.setValue('');
                    this.items = [...this.items, item];
                  });
              });
            }
          });
        } else {
          //adding script item from catalogue
          this.addScriptItem.setValue('');
          this.items = [...this.items, item];
        }
      }
    }
  }

  changePharmacy() {
    const dialogRef = this.dialog.open(ListPharmaciesDialog, {
      width: '30%',
      data: this.pharmaciesList,
      disableClose: true,
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.currentPharmacy = result;
        this.selectPharmacyStepControl.setValue(true);
        this.stepper.selectedIndex = 1;
      }
    });
  }


  //external add to cart function
  _addToCart() {
    try {
      this.loader.open('Adding items to cart...');
      let p = new Promise(async (resolve, reject) => {
        try {
          let response = [];
          for (let i = 0; i < this.items.length; i++) {
            const data = {
              //search user api to return health profile id
              userid: this.selectedUser.userid,
              profileid: this.selectedUser.profileid ? this.selectedUser.profileid : '',
              prescriptionid: this.items[i].itemID ? this.items[i].itemID : null,
              medlistid: '',
              name: this.items[i].details,
              form: '',
              strength: null,
              quantity: 0,
              prescriptiontype: this.items[i].isScript ? 3 : null,
              drugpack: this.items[i].quantity,
              scriptavailable: this.items[i].isScript ? 1 : 0,
              referrencetable: this.items[i].reference ? this.items[i].reference : null,
              price: this.items[i].unitPrice + this.items[i].markUp,
              image: this.items[i].image,
              itemschedule: this.items[i].itemSchedule,
              cardType: null,
              locationid: this.currentPharmacy.locationid,
              generic: 0,
              type: 2,
              Source: 'Medmate-Portal',
              by_source: true,
              custom_price: 1,
            };

            response[i] = await this.dataOrder.addToCart(data, this.token);
          }
          if (response.length == this.items.length) {
            const flattenResponse = [];
            for (let i = 0; i < response.length; i++) {
              flattenResponse[i] = response[i].cart[0];
              flattenResponse[i]['price'] = flattenResponse[i]['cartprice'];
              flattenResponse[i]['by_source'] = true;
              flattenResponse[i]['custom_price'] = 1;

            }
            this.cartItems = flattenResponse;
            resolve(flattenResponse);
          } else {
            this.loader.close();
            reject('Failed');
          }
        } catch (err) {
          this.loader.close();
          this.alertService.notifyError('Error: Unable to add items to cart. Please try again.');
        }
      });

      p.then((response: CartItem[]) => {
        const precheckoutPayload: PrecheckoutOrderPayload = {
          locationid: this.currentPharmacy.locationid,
          cartitems: response,
          deliveryaddressid: this.deliveryaddressid,
          ordertotal: this.calculateTotal(),
          type: 2,
          Source: 'Medmate-Portal',
        };
        this.dataOrder.precheckoutOrder(precheckoutPayload).subscribe(
          response => {
            this.loader.close();
            this.serviceFee = parseFloat(response.customerServiceFee);
            this.smallOrderFee = parseFloat(response.small_order_fee);
            this.stepper.next();
          },
          err => {
            this.loader.close();
            this.showMessage(
              err?.error?.msg || 'Oops! Something went wrong during precheckout.',
              'error'
            );
          }
        );
      }).catch(() => {
        this.loader.close();
        this.alertService.notifyError('Error: Unable to add items to cart. Please try again.');
      });
    } catch (err) {
      this.loader.close();
      this.alertService.notifyError('Error: Unable to add items to cart. Please try again.');
    }
  }

  placeOrder() {
    this.loader.open('Creating Order, Please wait...');
    let body: any = {
      locationid: this.currentPharmacy.locationid,
      devide_id: '',
      usermobile: this.customerDetailsForm.controls.mobile?.value,
      isGeneric: '',
      orderInstructions: '',
      AdditionalDeliveryInstructions: this.getDeliveryDetailsFormValue(
        'additionalDeliveryInstructions'
      ),
      speakwithpharmacist: '',
      estimateid: 1,
      timezone: this.timezones[this.currentPharmacy.state],
      deliveryaddressid: this.deliveryaddressid,
      deliveryfee: this.deliveryFee,
      customerServiceFee: this.serviceFee,
      deliveryinstructions: this.getDeliveryDetailsFormValue('deliveryInstruction'),
      small_order_fee: this.smallOrderFee,
      promocode: this.promocode,
      type: 2,
      orderTotal: this.calculateTotal(),
      Istbc: 1,
      Source: 'Medmate-Portal',
      by_source: true,
    };
    if (this.deliveryType.value == 'delivery') {
      const deliveryPartnerName = this.getDeliveryDetailsFormValue('deliveryPartner');
      const partnerOptionId = this.deliveryPartners.find(
        partner => partner.partnername == deliveryPartnerName
      ).partneroptionid;
      const today = new Date();
      const hours = today.getHours();
      const minutes = today.getMinutes();
      const meridian = hours >= 12 ? 'PM' : 'AM';
      const formattedHours = hours % 12 || 12;
      const formattedTime =
        formattedHours + ':' + (minutes < 10 ? '0' : '') + minutes + ' ' + meridian;

      body.userpreffereddate =
        today.getFullYear() + '-' + (today.getMonth() + 1) + '-' + today.getDate();
      body.userprefferedtime = formattedTime;
      body.ordertype = 'delivery';
      body.deliverypartnername = deliveryPartnerName;
      body.partneroptionid = partnerOptionId;
    } else {
      body.userpreffereddate = moment().add(1, 'days').format('YYYY-MM-DD');
      body.userprefferedtime = '6PM - 8PM',
        body.ordertype = 'Pick Up';
      body.deliverypartnername = 'Pick Up';
    }

    body['cartitems'] = this.cartItems;

    this.dataOrder.placeOrder(body, this.token).subscribe(
      res => {
        this.orderId = res.orderDetails.orderid;
        this.orderSuccessMessage = res.msg;
        const isAnyItemZeroCartPrice = this.cartItems.some(item => parseInt(item.cartprice, 10) === 0);
        this.changeOrderStatusSequence(this.orderId, res.orderDetails.ordertype, isAnyItemZeroCartPrice);
      },
      err => {
        this.loader.close();
        this.showMessage(err?.error?.msg, 'error');
      }
    );
  }

  closeDialog() {
    this.dialogRef.close();
  }
  nextStep(): void {
    if (this.items.length > 0) {
      this.addItemsStepControl.setValue(true);
    }
    if (this.jwtAuth.isAdmin() || this.jwtAuth.isBusiness()) {
      if (this.stepper.selectedIndex === 0 && this.selectPharmacyStepControl.invalid) {
        this.showMessage('Please select a pharmacy.', 'error');
        return;
      }

      if (this.stepper.selectedIndex === 2 && this.addItemsStepControl.invalid) {
        this.showMessage('Please add items to the order.', 'error');
        return;
      }

      if (this.addItemsStepControl.valid && this.stepper.selectedIndex === 2) {
        this._addToCart();
        return;
      }
    }

    if (this.jwtAuth.isPharmacyUser()) {
      if (this.stepper.selectedIndex === 1 && this.addItemsStepControl.invalid) {
        this.showMessage('Please add items to the order.', 'error');
        return;
      }

      if (this.addItemsStepControl.valid && this.stepper.selectedIndex === 1) {
        this._addToCart();
        return;
      }
    }

    this.stepper.next();
  }

  prevStep(): void {
    this.stepper.previous();
  }

  createAnotherOrder() {
    this.isOrderCreated = false;
    this.items = [];
    this.deliveryType.reset();
    this.groupedAllCustomerForms.reset();
    this.stepper.reset();
  }

  viewOrder() {
    this.closeDialog();
    this.router.navigate(['orders', 'vieworders', this.orderId]);
  }

  changeOrderStatusSequence(orderId: number, orderType: DeliveryType, isAnyItemZeroCartPrice: boolean = false) {
    const statusChangeSequence = this.getNextStatus(orderType, isAnyItemZeroCartPrice);

    if (statusChangeSequence.length === 0) {
      return;
    }

    this.changeOrderStatusRecursive(orderId, statusChangeSequence, 0);
  }

  changeOrderStatusRecursive(orderId: number, statusChangeSequence: OrderStatus[], index: number) {
    if (index >= statusChangeSequence.length) {
      this.loader.close();
      this.isOrderCreated = true;
      return;
    }

    const fromStatus = index === 0 ? OrderStatusEnum.New : statusChangeSequence[index - 1];
    const toStatus = statusChangeSequence[index];

    this.dataOrder.changeOrderStatus(orderId, fromStatus, toStatus, '')
      .subscribe(res => {
        this.changeOrderStatusRecursive(orderId, statusChangeSequence, index + 1);
      }, err => {
        this.loader.close();
      });
  }

  getNextStatus(orderType: DeliveryType, isAnyItemZeroCartPrice: boolean = false): OrderStatus[] {
    const deliveryStatus = isAnyItemZeroCartPrice
      ? [OrderStatusEnum.WaitingForReview]
      : [OrderStatusEnum.WaitingForReview, OrderStatusEnum.AwaitingAuthorisation];

    return orderType === DeliveryType.Delivery ? deliveryStatus : [OrderStatusEnum.Process];
  }

}

@Component({
  selector: 'consent-dialog',
  template: `
    <div mat-dialog-content>
      <h2>Script Item Disclaimer</h2>
      <mat-divider></mat-divider>
      <div class="mm-mt-2">
        Medmate does not accept any responsibility for obtaining prescriptions from patients
        and the pharmacy is responsible to obtain any required prescriptions from the
        patient.<br /><br />Please confirm that you have read and acknowledge the information
        in this disclaimer to add a script item to the order.
      </div>
    </div>
    <div mat-dialog-actions fxLayout="row" fxLayoutAlign="end center">
      <button mat-flat-button color="primary" (click)="onNoClick()">Close</button>
      <button mat-flat-button class="mm-bg-green mm-text-white mm-mt-10" (click)="onConfirm()">
        Accept
      </button>
    </div>
  `,
})
export class ConsentDialog {
  constructor(
    private dialogRef: MatDialogRef<ConsentDialog>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) { }
  onNoClick() {
    this.dialogRef.close();
  }

  onConfirm() {
    this.dialogRef.close(1);
  }
}

@Component({
  templateUrl: '../dialogs/select-schedule-dialog.html',
  styleUrls: ['../dialogs/select-schedule-dialog.scss'],
})
export class SelectScheduleDialog {
  scheduleSelect = new FormControl();
  schedules = [
    {
      name: 'S0',
      desc: 'Not Scheduled',
    },
    {
      name: 'S2',
      desc: 'Pharmacy Medicine',
    },
    {
      name: 'S3',
      desc: 'Pharmacist Only Medicine',
    },
    {
      name: 'S4',
      desc: 'Prescription Only Medicine',
    },
    {
      name: 'S8',
      desc: 'Controlled Drug',
    },
  ];

  constructor(
    private dialogRef: MatDialogRef<SelectScheduleDialog>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) { }
  onNoClick() {
    this.dialogRef.close('cancel');
  }

  onConfirm() {
    this.dialogRef.close(this.scheduleSelect.value);
  }
}

@Component({
  selector: 'upload-paper-script-dialog',
  templateUrl: '../dialogs/upload-paper-script-dialog.html',
  styleUrls: ['../dialogs/upload-paper-script-dialog.scss'],
})
export class UploadPaperScriptDialog implements OnInit {
  dragOver: boolean = false;
  uploading: boolean = false;
  file: File;
  PMA = new FormControl('1.5');
  base64Str;

  constructor(
    private dialogRef: MatDialogRef<UploadPaperScriptDialog>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialog: MatDialog,
    private _snackBar: MatSnackBar,
    private jwtAuth: JwtAuthService
  ) { }

  ngOnInit() { }

  log() {
    this.toBase64(this.file);
  }

  cancel() {
    const data = {
      schedule: this.data,
    };
    this.dialogRef.close(data);
  }

  toBase64(file) {
    return new Promise((resolve, reject) => {
      var reader = new FileReader();
      reader.onload = function () {
        resolve(reader.result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  onConfirm() {
    // this.uploading = true;
    // const formData = new FormData();
    // formData.append('newData', this.file);
    // if(this.admin){
    //   formData.append('locationid', this.data.chosenPharmacy.locationid);
    //   formData.append('PMA', this.PMA.value);
    // }else{
    //   formData.append('locationid', this.jwtAuth.getUser().locationid)
    // }
    // this.products.uploadCatalogue(formData).subscribe(res => {
    //   this.uploading = false;
    //   this.close({
    //     code: 1,
    //     msg: res.msg,
    //   });
    // }, err => {
    //   this.uploading = false;
    //   this._snackBar.openFromComponent(AlertSnackbarComponent, {
    //     panelClass: ['mat-bg-red'],
    //     horizontalPosition: 'center',
    //     verticalPosition: 'top',
    //     data: {
    //       msg: err.msg,
    //       action: 'dismiss',
    //     },
    //     duration: 2000
    //   })
    // })
    const data = {
      schedule: this.data,
      prescriptionimage: this.base64Str,
    };
    this.dialogRef.close(data);
  }

  close(data?) {
    this.dialogRef.close();
  }

  async uploadFile(e) {
    this.file = (e.target as HTMLInputElement).files[0];
    this.base64Str = await this.toBase64(this.file);
    this._snackBar.openFromComponent(AlertSnackbarComponent, {
      panelClass: ['mat-bg-primary'],
      horizontalPosition: 'center',
      verticalPosition: 'top',
      data: {
        msg: 'File added successfully',
        action: 'dismiss',
      },
      duration: 1400,
    });
  }

  async onDrop(e) {
    e.preventDefault();

    if (e.dataTransfer.items) {
      // Use DataTransferItemList interface to access the file(s)
      for (var i = 0; i < 1; i++) {
        // If dropped items aren't files, reject them
        if (
          e.dataTransfer.items[i].kind === 'file' &&
          e.dataTransfer.items[i].type.includes('image')
        ) {
          this.file = e.dataTransfer.items[i].getAsFile();
          this.base64Str = await this.toBase64(this.file);
          this._snackBar.openFromComponent(AlertSnackbarComponent, {
            panelClass: ['mat-bg-primary'],
            horizontalPosition: 'center',
            verticalPosition: 'top',
            data: {
              msg: 'File added successfully',
              action: 'dismiss',
            },
            duration: 1400,
          });
        } else {
          this._snackBar.openFromComponent(AlertSnackbarComponent, {
            panelClass: ['mat-bg-primary'],
            horizontalPosition: 'center',
            verticalPosition: 'top',
            data: {
              msg: 'Please check that you have uploaded an image file.',
              action: 'dismiss',
            },
            duration: 2000,
          });
        }
      }
    } else {
      // Use DataTransfer interface to access the file(s)
      for (var i = 0; i < 1; i++) {
        this.file = e.dataTransfer.files[0];
      }
    }
  }

  onDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
    this.dragOver = true;
  }

  onDragLeave(e) {
    this.dragOver = false;
  }
}
